import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { postToApi, streamChat } from '../../config';
import './Chatbot.css';

const WELCOME_TEXT = `Hello and welcome to M Square Professional! 👋
I am your dedicated AI Assistant.

I can help answer questions about:
• Growth Marketing & Local SEO.
• Business & Workflow Automation.
• Custom Web, Mobile & Cloud Software Development.

How can I help you streamline or grow your business today?`;

const MAX_MESSAGE_LENGTH = 1000;
const CONTACT_FALLBACK = 'You can also reach our team at info@msquareprofessionals.com.';

function createWelcomeMessage(time) {
  return {
    id: 'welcome-0',
    sender: 'bot',
    text: WELCOME_TEXT,
    time,
    isLiveTyping: false,
    showWelcomePrompts: true,
  };
}

function describeChatFailure(result) {
  if (result.status === 429) {
    return `You're sending messages a little too quickly. Please wait a moment and try again. ${CONTACT_FALLBACK}`;
  }
  if (result.data && result.data.error) {
    return `${result.data.error}`;
  }
  if (result.errorKind === 'timeout') {
    return `The assistant is taking longer than usual to respond. Please try again in a moment. ${CONTACT_FALLBACK}`;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'You appear to be offline. Please check your internet connection and try again.';
  }
  return `I'm having trouble connecting to the assistant right now. Please try again shortly. ${CONTACT_FALLBACK}`;
}

export default function ChatWindow({ isOpen, onClose }) {
  const [messages, setMessages] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('msq_chat_history') || 'null');
      if (Array.isArray(saved) && saved.length > 0) {
        // Restored messages are shown complete (no re-typing animation)
        return saved.map((m) => ({ ...m, isLiveTyping: false, isStreaming: false }));
      }
    } catch (e) {
      // Ignore sessionStorage / parse errors
    }
    return [createWelcomeMessage(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))];
  });

  const [isTyping, setIsTyping] = useState(false); // "thinking" indicator before the first words
  const [isResponding, setIsResponding] = useState(false); // true until the answer is complete
  const [isSubmittingEscalation, setIsSubmittingEscalation] = useState(false);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animatingClose, setAnimatingClose] = useState(false);
  // Synchronous guards against double submissions (state updates are async)
  const sendInFlight = useRef(false);
  const escalationInFlight = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setAnimatingClose(false);
    } else if (shouldRender) {
      setAnimatingClose(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setAnimatingClose(false);
      }, 260);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    try {
      sessionStorage.setItem('msq_chat_history', JSON.stringify(messages));
    } catch (e) {
      // Ignore sessionStorage errors
    }
  }, [messages]);

  function getCurrentTime() {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  const handleSendMessage = async (rawText) => {
    const text = (rawText || '').trim().slice(0, MAX_MESSAGE_LENGTH);
    if (!text || sendInFlight.current) return;
    sendInFlight.current = true;

    const userMsg = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text,
      time: getCurrentTime(),
    };

    // Recent conversation for context (only the fields the API needs)
    const history = messages
      .slice(-6)
      .map((m) => ({ sender: m.sender, text: m.text, isError: Boolean(m.isError) }));

    setMessages((prev) => [...prev, userMsg]);
    setIsTyping(true);
    setIsResponding(true);

    // Streaming: the answer bubble appears with the first words and grows as text arrives.
    // Text is revealed at a steady pace from a buffer, so the answer always flows in word by
    // word — even when a hosting proxy delivers the whole reply in one burst.
    const streamMsgId = `bot-${Date.now()}`;
    let streamStarted = false;
    let streamedText = '';
    let shownLength = 0;
    let revealing = false;
    let revealDoneResolve = null;
    const schedule = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (fn) => setTimeout(fn, 16);
    const revealStep = () => {
      const target = streamedText;
      if (shownLength >= target.length) {
        revealing = false;
        if (revealDoneResolve) revealDoneResolve();
        return;
      }
      const backlog = target.length - shownLength;
      // Large backlog (buffered burst) → catch up within ~0.6 s; live stream → follow closely
      let next = shownLength + Math.max(4, Math.ceil(backlog / 10));
      const space = target.indexOf(' ', next);
      if (space !== -1 && space - next < 10) next = space + 1; // finish the current word
      shownLength = Math.min(next, target.length);
      const visible = target.slice(0, shownLength);
      setMessages((prev) => prev.map((m) => (m.id === streamMsgId ? { ...m, text: visible } : m)));
      schedule(revealStep);
    };
    const waitForReveal = () => (revealing && shownLength < streamedText.length
      ? new Promise((resolve) => { revealDoneResolve = resolve; })
      : Promise.resolve());
    const onDelta = (chunk) => {
      streamedText += chunk;
      if (!streamStarted) {
        streamStarted = true;
        setIsTyping(false);
        setMessages((prev) => [...prev, {
          id: streamMsgId,
          sender: 'bot',
          text: '',
          time: getCurrentTime(),
          isLiveTyping: false,
          isStreaming: true,
        }]);
      }
      if (!revealing) {
        revealing = true;
        schedule(revealStep);
      }
    };

    let finalMsg;
    try {
      let result = await streamChat({ message: text, history }, { onDelta });
      if (result.errorKind === 'bad_response' && !streamStarted) {
        // Server without the streaming endpoint: use the classic JSON endpoint
        result = await postToApi('/chat', { message: text, history });
      }
      const data = result.data || {};

      if (data.answer) {
        finalMsg = {
          text: data.answer,
          category: data.category,
          ...(data.type === 'error' ? { isError: true } : {}),
          ...(data.needsEscalation ? { showEscalationForm: true, originalQuestion: text } : {}),
        };
      } else if (streamStarted) {
        finalMsg = {
          text: `${streamedText.trim()}\n\n(The connection was interrupted. Please ask again if this answer looks incomplete.)`,
        };
      } else {
        if (!result.ok) console.error('Chat API error:', result.status, result.errorKind || data.error);
        finalMsg = { text: describeChatFailure(result), isError: true };
      }
    } catch (error) {
      console.error('Unexpected chat error:', error);
      finalMsg = streamStarted
        ? { text: `${streamedText.trim()}\n\n(The connection was interrupted. Please ask again.)` }
        : { text: describeChatFailure({ status: 0, errorKind: 'network' }), isError: true };
    } finally {
      sendInFlight.current = false;
      setIsTyping(false);
      setIsResponding(false);
    }

    if (streamStarted) {
      // Let the visible text finish flowing in before showing the final answer / form
      await waitForReveal();
      setMessages((prev) => prev.map((m) => (m.id === streamMsgId
        ? { ...m, ...finalMsg, isStreaming: false, isLiveTyping: false }
        : m)));
    } else {
      // Instant (cached / non-streamed) answers: show with the quick word-by-word reveal
      setMessages((prev) => [...prev, {
        id: finalMsg.isError ? `bot-err-${Date.now()}` : streamMsgId,
        sender: 'bot',
        time: getCurrentTime(),
        isLiveTyping: true,
        ...finalMsg,
      }]);
    }
  };

  const handleQuickAction = (action, associatedQuestion = '') => {
    if (action === 'Talk to Our Team') {
      const escalationPrompt = {
        id: `bot-${Date.now()}`,
        sender: 'bot',
        text: "Please share your name, mobile number, and email address so our team can follow up with verified details.",
        time: getCurrentTime(),
        showEscalationForm: true,
        originalQuestion: associatedQuestion || 'General inquiry regarding MSquare solutions.',
        isLiveTyping: true,
      };
      setMessages((prev) => [...prev, escalationPrompt]);
      return;
    }
    handleSendMessage(action);
  };

  const handleSubmitEscalation = async (messageId, formData) => {
    if (escalationInFlight.current) return;
    escalationInFlight.current = true;
    setIsSubmittingEscalation(true);
    setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, escalationError: '' } : msg)));

    let errorText = '';
    try {
      const result = await postToApi('/escalation', formData, { timeoutMs: 20000 });
      const data = result.data || {};

      if (result.ok && data.success) {
        const reference = data.referenceId ? ` Your reference number is ${data.referenceId}.` : '';
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  escalationSubmitted: true,
                  escalationError: '',
                  text: `Thank you, ${formData.name}! Your request has been recorded.${reference} Our team will contact you shortly at ${formData.mobile} or ${formData.email}.`,
                }
              : msg
          )
        );
      } else if (result.status === 429) {
        errorText = data.error || 'Too many submissions. Please try again later or email info@msquareprofessionals.com.';
      } else if (data.error) {
        errorText = data.error;
      } else if (result.errorKind === 'timeout') {
        errorText = 'The request timed out. Please try again, or email info@msquareprofessionals.com.';
      } else {
        errorText = 'We could not reach our server. Please check your connection and try again, or email info@msquareprofessionals.com.';
      }
    } catch (err) {
      console.error('Escalation error:', err);
      errorText = 'Something went wrong while sending your details. Please try again, or email info@msquareprofessionals.com.';
    } finally {
      escalationInFlight.current = false;
      setIsSubmittingEscalation(false);
    }

    if (errorText) {
      setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, escalationError: errorText } : msg)));
    }
  };

  const handleTypingComplete = useCallback((msgId) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === msgId ? { ...msg, isLiveTyping: false } : msg
      )
    );
  }, []);

  const handleClearChat = () => {
    if (window.confirm('Reset conversation?')) {
      try {
        sessionStorage.removeItem('msq_chat_history');
      } catch (e) {
        // Ignore sessionStorage errors
      }
      setMessages([createWelcomeMessage(getCurrentTime())]);
    }
  };

  if (!shouldRender) return null;

  return (
    <div className={`msq-chat-container ${animatingClose ? 'msq-chat-closing' : 'msq-chat-entering'}`}>
      {/* Header */}
      <div className="msq-chat-header">
        <div className="msq-header-info">
          <div className="msq-header-avatar">
            <img
              src={`${process.env.PUBLIC_URL}/favicon.png`}
              alt="MSquare"
              width="24"
              height="24"
              className="msq-header-logo"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.nextElementSibling) {
                  e.currentTarget.nextElementSibling.style.display = 'block';
                }
              }}
            />
            <span className="msq-header-fallback-text" style={{ display: 'none' }}>M²</span>
          </div>
          <div>
            <div className="d-flex align-items-center gap-2">
              <h5 className="msq-header-title">MSquare Assistant</h5>
              <span className="msq-header-badge">AI</span>
            </div>
            <div className="msq-header-status">
              <span className="msq-status-dot"></span> Online • Verified Answers
            </div>
          </div>
        </div>
        <div className="msq-header-actions">
          <button
            type="button"
            onClick={handleClearChat}
            title="Reset conversation"
            aria-label="Reset chat"
            className="msq-btn-action"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"></polyline>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
            </svg>
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Minimize chat"
            aria-label="Minimize chat window"
            className="msq-btn-action msq-btn-minimize"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="5" y1="14" x2="19" y2="14"></line>
            </svg>
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close chat"
            aria-label="Close"
            className="msq-btn-action"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Messages List */}
      <ChatMessages
        messages={messages}
        isTyping={isTyping}
        isResponding={isResponding}
        onQuickActionClick={handleQuickAction}
        onSubmitEscalation={handleSubmitEscalation}
        isSubmittingEscalation={isSubmittingEscalation}
        onTypingComplete={handleTypingComplete}
      />

      {/* Input Controls */}
      <ChatInput
        onSendMessage={handleSendMessage}
        isTyping={isResponding}
        disabled={isSubmittingEscalation}
      />
    </div>
  );
}
